import { ArrowUpTrayIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';
import { useCreateBlobMutation } from '@/services/api/blobService';
import { useGetCollectionTreeQuery } from '@/services/api/collectionService';
import { useUploadPackageMutation } from '@/services/api/hipaaPackageService';

interface InlineHipaaUploadFormProps {
  matterId: string;
  providerId: string;
  requestId: string;
  onSuccess: () => void;
}

/**
 * Inline upload form for HIPAA PDF when no package exists.
 * Clean, minimal design inspired by Apple's file handling UX.
 */
export const InlineHipaaUploadForm = ({
  matterId,
  providerId,
  requestId,
  onSuccess,
}: InlineHipaaUploadFormProps) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<
    'idle' | 'uploading_blob' | 'creating_package'
  >('idle');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [createBlob] = useCreateBlobMutation();
  const [uploadPackage] = useUploadPackageMutation();
  const { data: collectionTree } = useGetCollectionTreeQuery(matterId, {
    skip: !matterId,
  });

  const isUploading = uploadProgress !== 'idle';

  const handleFileSelect = useCallback((file: File) => {
    if (file.type !== 'application/pdf') {
      toast.error('Please select a PDF file');
      return;
    }
    setSelectedFile(file);
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect],
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const file = e.dataTransfer.files[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect],
  );

  const handleUpload = useCallback(
    async (file: File) => {
      const rootCollection = collectionTree?.id;
      if (!rootCollection) {
        toast.error('Unable to determine collection');
        setSelectedFile(null);
        return;
      }

      try {
        setUploadProgress('uploading_blob');

        const formData = new FormData();
        formData.append('file', file);
        formData.append('name', file.name.replace('.pdf', ''));
        formData.append('original_filename', file.name);
        formData.append('collection', rootCollection);
        formData.append('file_mime_type', 'application/pdf');

        const createdBlob = await createBlob({
          matterId,
          blob: formData as any,
        }).unwrap();

        setUploadProgress('creating_package');

        await uploadPackage({
          matterId,
          providerId,
          requestId,
          body: {
            blob_id: createdBlob.id,
          },
        }).unwrap();

        toast.success('Package uploaded');
        setSelectedFile(null);
        setUploadProgress('idle');
        onSuccess();
      } catch (error) {
        console.error('Failed to upload HIPAA package:', error);
        toast.error('Failed to upload package');
        setSelectedFile(null);
        setUploadProgress('idle');
      }
    },
    [
      collectionTree,
      createBlob,
      uploadPackage,
      matterId,
      providerId,
      requestId,
      onSuccess,
    ],
  );

  // Auto-upload when file is selected
  useEffect(() => {
    if (selectedFile && uploadProgress === 'idle') {
      handleUpload(selectedFile);
    }
  }, [selectedFile, uploadProgress, handleUpload]);

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // File selected state - show uploading state (auto-uploads immediately)
  if (selectedFile) {
    return (
      <div className="rounded-xl border border-zinc-200 bg-zinc-50/50 p-4">
        <div className="flex items-center gap-3">
          {/* File icon */}
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-50">
            <DocumentTextIcon className="h-5 w-5 text-red-500" />
          </div>

          {/* File info */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-zinc-900 truncate">
              {selectedFile.name}
            </p>
            <p className="text-xs text-zinc-500">
              {formatFileSize(selectedFile.size)}
            </p>
          </div>

          {/* Uploading state */}
          <div className="flex items-center gap-2 text-sm text-zinc-500">
            <Loader2 className="h-4 w-4 animate-spin text-indigo-500" />
            <span>
              {uploadProgress === 'uploading_blob'
                ? 'Uploading...'
                : 'Processing...'}
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Default state - drop zone
  return (
    <div
      role="button"
      tabIndex={0}
      className={cn(
        'rounded-xl border-2 border-dashed p-6 transition-all duration-150 cursor-pointer',
        isDragging
          ? 'border-indigo-400 bg-indigo-50/50'
          : 'border-zinc-200 hover:border-zinc-300 bg-zinc-50/30 hover:bg-zinc-50/50',
      )}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={() => fileInputRef.current?.click()}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          fileInputRef.current?.click();
        }
      }}
    >
      <div className="flex flex-col items-center justify-center text-center">
        <div
          className={cn(
            'flex h-10 w-10 items-center justify-center rounded-full mb-3 transition-colors',
            isDragging ? 'bg-indigo-100' : 'bg-zinc-100',
          )}
        >
          <ArrowUpTrayIcon
            className={cn(
              'h-5 w-5 transition-colors',
              isDragging ? 'text-indigo-600' : 'text-zinc-400',
            )}
          />
        </div>
        <p
          className={cn(
            'text-sm font-medium transition-colors',
            isDragging ? 'text-indigo-600' : 'text-zinc-600',
          )}
        >
          {isDragging ? 'Drop to upload' : 'Upload HIPAA Package'}
        </p>
        <p className="text-xs text-zinc-400 mt-1">
          Drag and drop or click to browse
        </p>
      </div>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleInputChange}
        className="hidden"
        accept=".pdf,application/pdf"
        disabled={isUploading}
      />
    </div>
  );
};
